import { isCdkProxyConfigured } from '../lib/appConfig';
import { fetchCdkProxy, splitProxyUrl } from '../lib/cdkProxy';

const API_URL = process.env.REACT_APP_NUA_TALKER_NTH_VOICE;

async function requestDirect({ url, method, data, signal, headers }) {
  const response = await fetch(`${API_URL}${url}`, {
    method,
    headers: {
      ...headers,
    },
    body:
      method && method.toUpperCase() !== 'GET' && data
        ? JSON.stringify(data)
        : undefined,
    signal,
  });

  if (!response.ok) {
    let errorMessage = `${response.status} ${response.statusText}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody?.message || errorBody?.error || errorMessage;
    } catch (_) {
      /* respuesta de error sin JSON */
    }
    const error = new Error(errorMessage);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

async function request({ url, method, data, signal, hubSpotId, headers }) {
  if (isCdkProxyConfigured() && hubSpotId) {
    const { path, queryParams } = splitProxyUrl(url);
    return fetchCdkProxy({
      service: 'nthvoice',
      hubSpotId,
      path,
      method: method || 'GET',
      body: data,
      queryParams,
    });
  }
  return requestDirect({ url, method, data, signal, headers });
}

export async function getLogsByClientId({ queryObject, clientId }) {
  const queryParams = new URLSearchParams(queryObject).toString();
  return request({
    url: `/campaigns/logs/${clientId}?${queryParams}`,
    method: 'GET',
    hubSpotId: clientId,
  });
}

export async function getQueuesByClientId({ clientId, service }) {
  return request({
    url: `/queues/${clientId}/${service}/`,
    method: 'GET',
    hubSpotId: clientId,
  });
}

export async function searchLogsByClientId({ clientId, body, signal }) {
  return request({
    url: `/campaigns/logs/${clientId}/search`,
    method: 'POST',
    data: body,
    signal,
    hubSpotId: clientId,
  });
}

export async function searchStatusCountsByClientId({ clientId, body, signal }) {
  return request({
    url: `/campaigns/logs/${clientId}/search/status-counts`,
    method: 'POST',
    data: body,
    signal,
    hubSpotId: clientId,
  });
}

export async function postQueueAction({
  id,
  service,
  action,
  queueUrl,
  signal,
}) {
  return request({
    url: `/queues/${id}/${service}/${action}`,
    method: 'POST',
    data: { action, service, queueUrl },
    signal,
    hubSpotId: id,
  });
}

export async function getSchedulerStatus({ clientId, service }) {
  return request({
    url: `/schedulers/${clientId}/${service}`,
    method: 'GET',
    hubSpotId: clientId,
  });
}

async function limitsRequest({ url, method, data, hubSpotId }) {
  const isGet = !method || method.toUpperCase() === 'GET';
  return request({
    url,
    method: method || 'GET',
    data,
    hubSpotId,
    headers: isGet ? {} : { 'Content-Type': 'application/json' },
  });
}

export async function getCallBlastingLimitBags({ clientId }) {
  return limitsRequest({
    url: `/limits/${clientId}`,
    method: 'GET',
    hubSpotId: clientId,
  });
}

export async function createCallBlastingLimitBag({ clientId, bag }) {
  return limitsRequest({
    url: `/limits/${clientId}`,
    method: 'POST',
    data: bag,
    hubSpotId: clientId,
  });
}

export async function updateCallBlastingLimitBag({ clientId, metric, bag }) {
  return limitsRequest({
    url: `/limits/${clientId}/${metric}`,
    method: 'PUT',
    data: bag,
    hubSpotId: clientId,
  });
}

export async function deleteCallBlastingLimitBag({ clientId, metric }) {
  return limitsRequest({
    url: `/limits/${clientId}/${metric}`,
    method: 'DELETE',
    hubSpotId: clientId,
  });
}

export async function getCallBlastingDailySummary({
  clientId,
  startDate,
  endDate,
  campaignName,
  service,
}) {
  const query = { startDate, endDate };
  if (campaignName) query.campaignName = campaignName;
  if (service) query.service = service;
  const queryString = new URLSearchParams(query).toString();
  return limitsRequest({
    url: `/reporting/${clientId}/daily-summary?${queryString}`,
    method: 'GET',
    hubSpotId: clientId,
  });
}

export async function getReportingCampaigns({ clientId }) {
  return limitsRequest({
    url: `/reporting/${encodeURIComponent(clientId)}/campaigns`,
    method: 'GET',
    hubSpotId: clientId,
  });
}
